import { useCallback, useState, FC } from 'react'
import { useCreateGroupMutation } from 'src/app/appApiQueries'
import { setType } from 'src/app/modal'
import { useAppDispatch, useAppSelector } from 'src/hooks'
import ModalTitle from '../ModalTitle'
import ModalButtons from '../ModalButtons'
import useModalLoading from 'src/hooks/useModalLoading'
import { selectGroupNames } from 'src/app/hostSettings'
import Input from 'src/components/Input'
import { nameOnObject } from 'src/utils/helpers'

type GroupCreateModalProps = {
  setSelectedGroupOnCreation?: (groupId: string) => void
}

export const GroupCreateModal: FC<GroupCreateModalProps> = ({
  setSelectedGroupOnCreation,
}) => {
  const groupNames = useAppSelector(selectGroupNames)
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const [createGroup, { isLoading, isSuccess, isError }] =
    useCreateGroupMutation()

  useModalLoading({
    isLoading,
    isSuccess,
    isError,
  })

  const onCreate = useCallback(() => {
    if (!nameOnObject(value, groupNames)) {
      // create group then close the modal
      createGroup(value)
        .unwrap()
        .then(({ newGroupId }) => {
          if (setSelectedGroupOnCreation) setSelectedGroupOnCreation(newGroupId)
        })
    } else {
      setError('This group name already exists')
    }
  }, [createGroup, groupNames, setSelectedGroupOnCreation, value])

  const onCancel = () => {
    dispatch(setType({ type: null }))
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    // clear an error first if we have one
    if (error) setError('')
    if (e.target.value.length > 30) {
      setError('Must be less than 30 characters')
    }
  }

  const disabled = !value || !!error

  return (
    <div>
      <ModalTitle text="Create a New Tag" />

      <p aria-label="group-name" className="text-sm">
          Tag
      </p>
      <Input
        labeledBy="group-name"
        value={value}
        onChange={onChange}
        error={error}
      />

      <ModalButtons
        firstButton={{
          name: 'Cancel',
          type: 'secondary',
          onClick: onCancel,
          htmlButtonType: 'button',
        }}
        secondButton={{
          name: 'Create',
          type: 'primary',
          onClick: onCreate,
          disabled,
          htmlButtonType: 'button',
        }}
      />
    </div>
  )
}
